import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {Lock, LogOut} from 'lucide-react';
import {Card, TaskRow} from '@chekinapp/ui';
import {PATHS} from 'routes/constants';
import {usePath} from 'hooks';
import {SectionHeading} from 'components/SectionHeading';
import {POSTHOG_EVENTS, useAnalytics} from 'analytics';
import {useCheckoutSettings} from 'pages/Checkout/hooks/useCheckoutSettings';
import {useCheckoutSchedule} from 'pages/Checkout/hooks/useCheckoutSchedule';

export function CheckoutChecklist({className}: {className?: string}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {track} = useAnalytics();
  const {isCheckoutEnabled, isAvailable, isCheckoutCompleted} = useCheckoutSettings();
  const {dateLabel} = useCheckoutSchedule();

  if (!isCheckoutEnabled) return null;

  const handleOpen = () => {
    track(POSTHOG_EVENTS.HOME_NAVIGATION, {
      source: 'checkout_checklist',
      destination: 'checkout',
    });
    navigate(getFullPath(PATHS.checkout.main));
  };

  return (
    <section data-testid="checkout-checklist" className={className}>
      <SectionHeading className="mb-3">{t('complete_your_check_out')}</SectionHeading>

      <Card className="overflow-hidden p-0">
        <TaskRow
          title={t('check_out_short')}
          description={
            isCheckoutCompleted
              ? t('online_checkout_is_completed', {ns: 'check-out'})
              : isAvailable
              ? t('check_out_instructions_ready')
              : t('check_out_final_summary')
          }
          state={isCheckoutCompleted ? 'done' : isAvailable ? 'next' : 'locked'}
          icon={<LogOut />}
          chipIcon={
            isCheckoutCompleted || isAvailable ? undefined : <Lock strokeWidth={2.4} />
          }
          chipLabel={
            isCheckoutCompleted
              ? undefined
              : isAvailable
              ? t('status_open')
              : dateLabel
              ? t('check_out_opens_on', {ns: 'check-out', date: dateLabel})
              : undefined
          }
          onClick={isAvailable ? handleOpen : undefined}
        />
      </Card>
    </section>
  );
}
